import React, {useCallback, useState} from 'react';

import {AddToFolderPopover} from 'app/folders/components/AddToFolderPopover';
import {DeleteDocumentModal} from 'app/general/modals/DeleteDocumentModal';
import {BaseActionableDocumentType} from 'app/general/types';
import {
  ActionButtonBuilderType,
  ActionMenuItemType,
  ActionsButton,
} from 'app/general/widgets/ActionsButton';
import {gt} from 'app/utils/i18n';

export type ActionsContextType =
  | 'edit'
  | 'importing'
  | 'view-published'
  | 'dashboard'
  | 'folders';

interface ActionDefinitionType {
  icon: string;
  text: string;
  callback: () => unknown;
  condition?: () => boolean;
  hideContexts?: Array<ActionsContextType>;
}

interface Props {
  children?: React.ReactElement | ActionButtonBuilderType;
  pageContext: ActionsContextType;
  document: BaseActionableDocumentType;
  refreshData: () => unknown;
  currentFolder?: void | string;
}

/* a button which opens a dropdown menu with avilable actions for the current document */
export function DocumentActionsButton({
  children,
  pageContext,
  document,
  refreshData,
  currentFolder,
}: Props): JSX.Element {
  const [isShowing, setShow] = useState<null | 'folders' | 'delete'>(null);

  const close = () => setShow(null);

  const [currButtonRef, setCurrButtonRef] = useState<null | HTMLElement>(null);

  const setButtonRef = useCallback((newButtonRef) => {
    if (newButtonRef != currButtonRef) {
      setCurrButtonRef(newButtonRef);
    }
  }, []);

  const doc =
    document.content_type === 'document'
      ? {
          id: document.object_id,
          ct: document.document_content_type,
          hashid: document.document.hashid,
          url: document.document.url,
          versionct: document.document.current_version.content_type,
          versionid: document.document.current_version.id,
        }
      : {
          id: document.id,
          ct: document.content_type,
          hashid: document.hashid,
          url: document.url,
          versionct: document.current_version.content_type,
          versionid: document.current_version.id,
        };

  const availableActions = React.useMemo(() => {
    const {can_edit, can_delete} = document;

    const actionsDefinitions: Array<ActionDefinitionType> = [
      {
        icon: 'far fa-folder',
        text: gt('Folders'),
        callback: () => setShow('folders'),
        condition: () => can_edit,
        hideContexts: ['importing'],
      },
      {
        icon: 'far fa-trash',
        text: gt('Move to Recycle Bin'),
        callback: () => setShow('delete'),
        condition: () => can_delete,
        hideContexts: ['importing'],
      },
    ];

    return actionsDefinitions
      .map((item) => ({
        key: item.icon,
        condition: (): boolean => true,
        hideContexts: [] as Array<ActionsContextType>,
        ...item,
      }))
      .filter(
        (item) =>
          item.condition() && item.hideContexts.indexOf(pageContext) === -1
      )
      .map(
        ({icon, text, callback, key}): ActionMenuItemType => ({
          icon,
          text,
          callback,
          key,
        })
      );
  }, [document, pageContext]);

  // these props are used on all popovers
  const popoversOverlayProps = {
    target: currButtonRef as HTMLElement,
    trigger: 'legacy',
    placement: 'bottom' as const,
    isOpen: true,
    toggle: close,
  };

  const promisedCallback = () =>
    Promise.resolve(refreshData != null ? refreshData() : null);

  return (
    <>
      <ActionsButton ref={setButtonRef} actions={availableActions}>
        {children}
      </ActionsButton>
      {isShowing == null || currButtonRef == null ? null : isShowing ===
        'delete' ? (
        <DeleteDocumentModal
          show={true}
          object={{
            url: doc.url,
            name: document.name,
            content_type: doc.ct,
          }}
          redirectContentType={doc.ct}
          onClose={close}
          onDelete={pageContext === 'dashboard' ? promisedCallback : undefined}
        />
      ) : isShowing === 'folders' && doc.ct !== 'quiz' ? (
        <AddToFolderPopover
          overlayProps={{...popoversOverlayProps, trigger: 'click'}}
          onSuccess={promisedCallback}
          documents={[
            {
              object_id: doc.id,
              document_content_type: doc.ct,
              url: doc.url,
            },
          ]}
          currentFolder={currentFolder}
        />
      ) : null}
    </>
  );
}
